import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Pencil } from "lucide-react";
import { ImportButton } from "@/components/ImportDialog";
import { toast } from "sonner";

export default function Artigos() {
  const [list, setList] = useState([]);
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ descricao: "", ref_marca: "", marca: "", ref_toconline: "", familia: "" });

  const load = () => api.get("/artigos", { params: { q } }).then((r) => setList(r.data));
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const emptyForm = { descricao: "", ref_marca: "", marca: "", ref_toconline: "", familia: "" };
  const openCreate = () => { setEditId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (a) => {
    setEditId(a.id);
    setForm({ descricao: a.descricao || "", ref_marca: a.ref_marca || "", marca: a.marca || "", ref_toconline: a.ref_toconline || "", familia: a.familia || "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editId) {
        await api.put(`/artigos/${editId}`, form);
        toast.success("Artigo atualizado.");
      } else {
        const { data } = await api.post("/artigos", form);
        if (data._alerta) toast.warning(data._alerta);
        else toast.success("Artigo criado.");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditId(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-900">Artigos & Referências</h1>
        <div className="flex gap-2">
          <ImportButton entity="artigos" label="artigos" onDone={load} />
          <Button className="bg-sky-600 hover:bg-sky-700 gap-1.5" data-testid="btn-novo-artigo" onClick={openCreate}><Plus size={16} /> Novo artigo</Button>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editId ? "Editar artigo" : "Novo artigo"}</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500 -mt-1">Ref. da marca e ref. Toconline são campos distintos. Artigos podem ficar provisórios.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Descrição</Label><Input className="uppercase" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value.toUpperCase() })} data-testid="artigo-descricao" /></div>
              <div><Label className="text-xs">Ref. marca</Label><Input className="font-mono uppercase" value={form.ref_marca} onChange={(e) => setForm({ ...form, ref_marca: e.target.value.replace(/\s+/g, "").toUpperCase() })} data-testid="artigo-ref-marca" /></div>
              <div><Label className="text-xs">Marca</Label><Input value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} data-testid="artigo-marca" /></div>
              <div><Label className="text-xs">Ref. Toconline</Label><Input className="font-mono" value={form.ref_toconline} onChange={(e) => setForm({ ...form, ref_toconline: e.target.value })} /></div>
              <div><Label className="text-xs">Família</Label><Input value={form.familia} onChange={(e) => setForm({ ...form, familia: e.target.value })} placeholder="ex.: 01.2" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid="btn-guardar-artigo">{editId ? "Atualizar" : "Guardar"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mb-3 max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Descrição, referência…" className="pl-9" data-testid="artigos-search" />
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
            <tr><th className="text-left px-4 py-2 font-semibold">Descrição</th><th className="text-left px-4 py-2 font-semibold">Ref. marca</th><th className="text-left px-4 py-2 font-semibold">Marca</th><th className="text-left px-4 py-2 font-semibold">Ref. Toconline</th><th className="text-left px-4 py-2 font-semibold">Família</th><th className="text-left px-4 py-2 font-semibold">Estado</th><th className="text-right px-4 py-2 font-semibold">Ações</th></tr>
          </thead>
          <tbody>
            {list.map((a) => (
              <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`artigo-row-${a.id}`}>
                <td className="px-4 py-2 font-medium text-slate-800">{a.descricao}</td>
                <td className="px-4 py-2 font-mono text-slate-600">{a.ref_marca || "—"}</td>
                <td className="px-4 py-2 text-slate-600">{a.marca || "—"}</td>
                <td className="px-4 py-2 font-mono text-slate-600">{a.ref_toconline || "—"}</td>
                <td className="px-4 py-2 text-slate-500">{a.familia || "—"}</td>
                <td className="px-4 py-2">{a.provisorio ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Provisório</span> : <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Toconline</span>}</td>
                <td className="px-4 py-2 text-right"><Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => openEdit(a)} data-testid={`artigo-edit-${a.id}`}><Pencil size={12} /> Editar</Button></td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={7} className="text-center text-slate-400 py-8">Sem artigos.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
